import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Check, ArrowUp, ArrowDown, Loader2, Sparkles, Clock, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { billingApi } from "../../lib/api";
import EmbeddedStripeCheckoutModal from "../payments/EmbeddedStripeCheckoutModal";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const prettyModule = (m) =>
  String(m).replace(/[_-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Tenant plan comparison + enrollment. Mirrors the plans configured in the
 * Platform. Upgrades open Stripe embedded checkout inline; downgrades + term
 * switches are scheduled for the next renewal.
 */
export function PlanComparison({ current, onChanged }) {
  const [data, setData] = useState(null);
  const [term, setTerm] = useState(current?.term || "monthly");
  const [busyId, setBusyId] = useState(null);
  const [confirmDowngrade, setConfirmDowngrade] = useState(null); // { plan, lostModules }
  const [checkout, setCheckout] = useState(null); // { clientSecret, sessionId, plan }

  useEffect(() => {
    billingApi.plans().then(setData).catch(() => setData({ plans: [] }));
  }, []);

  const currentOrder = useMemo(() => {
    const cp = (data?.plans || []).find((p) => p.isCurrent);
    return cp ? (cp.order ?? 0) : -1;
  }, [data]);

  const currentPlan = (data?.plans || []).find((p) => p.isCurrent);

  const onComplete = useCallback(async () => {
    const sid = checkout?.sessionId;
    if (!sid) { setCheckout(null); return; }
    for (let i = 0; i < 6; i += 1) {
      const s = await billingApi.status(sid).catch(() => null);
      if (s?.payment_status === "paid") {
        toast.success(`Payment received — you're on ${checkout.plan?.name || "your new plan"}!`);
        setCheckout(null);
        setBusyId(null);
        onChanged && onChanged();
        billingApi.plans().then(setData).catch(() => {});
        return;
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    toast.message("Payment still processing. Refresh in a moment.");
    setCheckout(null);
    setBusyId(null);
  }, [checkout, onChanged]);

  if (!data) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 flex items-center justify-center text-slate-400 text-[13px]" data-testid="plan-comparison-loading">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading plans…
      </div>
    );
  }

  const enrollNow = async (plan) => {
    setBusyId(plan.id);
    try {
      const res = await billingApi.checkout(window.location.origin, {
        planId: plan.id, term, seats: data.seats, embedded: true,
      });
      if (res?.client_secret) {
        setCheckout({ clientSecret: res.client_secret, sessionId: res.session_id, plan, amount: res.amount });
      } else if (res?.url) {
        window.location.href = res.url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setBusyId(null);
    }
  };

  const scheduleDowngrade = async (plan) => {
    setBusyId(plan.id);
    try {
      await billingApi.scheduleChange({ planId: plan.id, term });
      toast.success(`Downgrade to ${plan.name} scheduled for your next renewal.`);
      setConfirmDowngrade(null);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not schedule the change.");
    } finally {
      setBusyId(null);
    }
  };

  const handleAction = (plan) => {
    const isUpgrade = currentOrder < 0 || (plan.order ?? 0) > currentOrder;
    if (isUpgrade) return enrollNow(plan);
    // Downgrade — warn about any modules they'd lose, but allow.
    const lost = (currentPlan?.modules || []).filter((m) => !(plan.modules || []).includes(m));
    if (lost.length) return setConfirmDowngrade({ plan, lostModules: lost });
    return scheduleDowngrade(plan);
  };

  return (
    <>
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6" data-testid="plan-comparison">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[15px] font-bold text-slate-900 flex items-center gap-2"><Sparkles className="h-4 w-4 text-blue-600" /> Plans &amp; pricing</div>
          <div className="text-[12.5px] text-slate-500 mt-0.5">Upgrade now (charged today) or downgrade at your next renewal. Estimated totals use your {data.seats} seats.</div>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50" data-testid="plan-term-toggle">
          {["monthly", "annual"].map((t) => (
            <button
              key={t}
              onClick={() => setTerm(t)}
              data-testid={`plan-term-${t}`}
              className={`px-3 py-1.5 rounded-md text-[12.5px] font-semibold capitalize transition-colors ${term === t ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
            >
              {t}{t === "annual" && <span className="ml-1 text-[10px] text-emerald-600">save</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6">
        {data.plans.map((plan) => {
          const price = term === "annual" ? plan.perSeatAnnual : plan.perSeatMonthly;
          const estTotal = term === "annual" ? plan.estAnnualTotal : plan.estMonthlyTotal;
          const isCurrent = plan.isCurrent && term === data.currentTerm;
          const isUpgrade = currentOrder < 0 || (plan.order ?? 0) > currentOrder;
          const sameTermSwitch = plan.isCurrent && term !== data.currentTerm;
          return (
            <div
              key={plan.id}
              data-testid={`plan-card-${plan.id}`}
              className={`relative rounded-xl border-2 p-5 flex flex-col transition-colors ${plan.isCurrent ? "border-blue-500 bg-blue-50/30" : "border-slate-200 hover:border-slate-300"}`}
            >
              {plan.isCurrent && (
                <span className="absolute -top-2.5 left-4 px-2 py-0.5 rounded-full bg-blue-600 text-white text-[10.5px] font-bold uppercase tracking-wide" data-testid={`plan-current-badge-${plan.id}`}>Current plan</span>
              )}
              <div className="text-[16px] font-bold text-slate-900">{plan.name}</div>
              <div className="mt-1.5 flex items-baseline gap-1">
                <span className="text-[26px] font-extrabold text-slate-900">{money(price)}</span>
                <span className="text-[12.5px] text-slate-500">/seat/{term === "annual" ? "yr" : "mo"}</span>
              </div>
              <div className="text-[11.5px] text-slate-400 mt-0.5">
                {term === "annual" ? `${money(plan.perSeatMonthly)}/seat/mo billed monthly` : `${money(plan.perSeatAnnual)}/seat/yr if annual`}
              </div>
              <div className="mt-2 text-[12.5px] text-slate-600">
                Est. <span className="font-bold text-slate-800">{money(estTotal)}</span>/{term === "annual" ? "yr" : "mo"} for {plan.estSeats} seats
              </div>

              {Array.isArray(plan.features) && plan.features.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {plan.features.slice(0, 6).map((f, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[12px] text-slate-600">
                      <Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              )}
              {Array.isArray(plan.modules) && plan.modules.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1" data-testid={`plan-modules-${plan.id}`}>
                  {plan.modules.map((m) => (
                    <span key={m} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-medium">{prettyModule(m)}</span>
                  ))}
                </div>
              )}

              <div className="mt-auto pt-4">
                {isCurrent ? (
                  <button disabled data-testid={`plan-action-${plan.id}`} className="w-full h-10 rounded-lg bg-slate-100 text-slate-400 text-[13px] font-semibold cursor-default">
                    Current plan
                  </button>
                ) : sameTermSwitch ? (
                  <button
                    onClick={() => scheduleDowngrade(plan)}
                    disabled={busyId === plan.id}
                    data-testid={`plan-action-${plan.id}`}
                    className="w-full h-10 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-[13px] font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                  >
                    {busyId === plan.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />} Switch to {term} at renewal
                  </button>
                ) : isUpgrade ? (
                  <button
                    onClick={() => handleAction(plan)}
                    disabled={busyId === plan.id}
                    data-testid={`plan-action-${plan.id}`}
                    className="w-full h-10 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                  >
                    {busyId === plan.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />} Upgrade &amp; pay
                  </button>
                ) : (
                  <button
                    onClick={() => handleAction(plan)}
                    disabled={busyId === plan.id}
                    data-testid={`plan-action-${plan.id}`}
                    className="w-full h-10 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 text-[13px] font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                  >
                    {busyId === plan.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowDown className="h-4 w-4" />} Downgrade at renewal
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Downgrade module-loss warning */}
      {confirmDowngrade && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-4" onClick={() => setConfirmDowngrade(null)} data-testid="downgrade-warn-modal">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-100">
              <div className="h-10 w-10 rounded-full bg-amber-50 flex items-center justify-center shrink-0"><AlertTriangle className="h-5 w-5 text-amber-600" /></div>
              <div className="flex-1">
                <div className="text-[15px] font-bold text-slate-900">Downgrade to {confirmDowngrade.plan.name}?</div>
                <div className="text-[12.5px] text-slate-500 mt-0.5">This takes effect at your next renewal. You&apos;ll lose access to:</div>
              </div>
              <button onClick={() => setConfirmDowngrade(null)} className="h-7 w-7 rounded-md hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4 text-slate-400" /></button>
            </div>
            <div className="px-5 py-4">
              <div className="flex flex-wrap gap-1.5">
                {confirmDowngrade.lostModules.map((m) => (
                  <span key={m} className="px-2 py-1 rounded-md bg-red-50 text-red-700 text-[12px] font-medium border border-red-100">{prettyModule(m)}</span>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-100">
              <button onClick={() => setConfirmDowngrade(null)} data-testid="downgrade-cancel" className="h-9 px-4 rounded-lg border border-slate-200 text-[13px] font-medium hover:bg-slate-100">Keep current plan</button>
              <button onClick={() => scheduleDowngrade(confirmDowngrade.plan)} disabled={busyId === confirmDowngrade.plan.id} data-testid="downgrade-confirm" className="h-9 px-4 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-[13px] font-semibold disabled:opacity-50">
                Schedule downgrade
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    {checkout && (
      <EmbeddedStripeCheckoutModal
        clientSecret={checkout.clientSecret}
        title={`Upgrade to ${checkout.plan?.name || "new plan"}`}
        subtitle={`${money(checkout.amount)} · ${term === "annual" ? "Annual" : "Monthly"}`}
        onComplete={onComplete}
        onClose={() => { setCheckout(null); setBusyId(null); }}
        testid="plan-embedded-checkout"
      />
    )}
    </>
  );
}

export default PlanComparison;
